import { NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, input, signal, viewChild } from '@angular/core';

import { CamTranslationFiles, DocumentsListComponent, DocumentsSectionsHelper } from '@camelot/files-basic';
import { SearchFieldComponent, UploadComponent } from '@camelot/form-input';
import { InputTextBox, InputUpload, InputUploadValue } from '@camelot/form-model';
import { NavigationComponent } from '@camelot/menu';
import { TranslatePipe } from '@camelot/translation';
import {
  ButtonComponent,
  LayoutSideComponent,
  LayoutSideContentComponent,
  LayoutSideCtaComponent,
  LinkComponent,
  LoaderComponent,
  TitleComponent,
} from '@camelot/ui';
import { CamBaseModal } from '@camelot/utils';

export type AttachmentsResult = {
  result: InputUploadValue[];
};

export type DocumentsAttachmentsModalInput<K extends string = string> = {
  documentsIds: Record<K, string[]>;
  initialAttachments: InputUploadValue[];
};

export const UPLOADED_DOCUMENTS_KEY = 'uploaded';

export enum EAttachmentsModalView {
  newDocuments = 'newDocuments',
  linkedDocuments = 'linkedDocuments',
}

@Component({
  selector: 'cam-documents-attachments-modal-content',
  imports: [
    ButtonComponent,
    DocumentsListComponent,
    LayoutSideComponent,
    LayoutSideContentComponent,
    LayoutSideCtaComponent,
    LinkComponent,
    LoaderComponent,
    NavigationComponent,
    NgTemplateOutlet,
    SearchFieldComponent,
    TitleComponent,
    TranslatePipe,
    UploadComponent,
  ],
  templateUrl: './documents-attachments-modal.component.html',
  styleUrl: './documents-attachments-modal.component.scss',
})
export class DocumentsAttachmentsModal<K extends string>
  extends CamBaseModal<DocumentsAttachmentsModalInput<K>, AttachmentsResult>
  implements OnInit
{
  sectionsHelper = input.required<DocumentsSectionsHelper<K>>();

  documentsList = viewChild(DocumentsListComponent);

  public uploadInput = new InputUpload({
    confirmButton: false,
  });

  public allUploadsCompleted = signal(true);

  public searchInput = new InputTextBox();

  public search = signal('');

  public view = EAttachmentsModalView.linkedDocuments;

  public EAttachmentsModalView = EAttachmentsModalView;
  public UPLOADED_DOCUMENTS_KEY = UPLOADED_DOCUMENTS_KEY;

  constructor() {
    super();
    CamTranslationFiles.getInstance();
  }

  ngOnInit() {
    this.uploadInput.value = this.sectionsHelper().init(this.documentsIds(), this.initialAttachments());
  }

  public selectedDocuments(): InputUploadValue[] {
    return [...this.sectionsHelper().selected(), ...this.uploadInput.value];
  }

  public documentsIds(): Record<K, string[]> {
    return this.modalState()?.input()?.documentsIds ?? ({} as Record<K, string[]>);
  }

  public initialAttachments(): InputUploadValue[] {
    return this.modalState()?.input()?.initialAttachments ?? [];
  }

  public onSearch(value: string) {
    this.search.set(value ?? '');
  }

  public updateUploadStatus(status: any) {
    this.allUploadsCompleted.set(status);
  }

  public removedUploadDocument(removedId: string) {
    this.uploadInput.value = this.uploadInput.value.filter(v => v.id !== removedId);
  }

  public canSelectAll() {
    const list = this._selectableList();
    return !!list && list.hasDocuments() && !list.areAllChecked();
  }

  public canUnselectAll() {
    return this._selectableList()?.hasChecked() ?? false;
  }

  public selectAll() {
    this._selectableList()?.checkAll();
  }

  public unselectAll() {
    this._selectableList()?.uncheckAll();
  }

  public switchView(view: EAttachmentsModalView) {
    this.view = view;
  }

  public confirm() {
    if (this.view === EAttachmentsModalView.newDocuments) {
      this.switchView(EAttachmentsModalView.linkedDocuments);
      return;
    }
    const result: AttachmentsResult = { result: this.selectedDocuments() };
    this.modalState()?.completed(result);
    this.closeEvent.emit(result);
  }

  public close() {
    if (this.view === EAttachmentsModalView.newDocuments) {
      this.switchView(EAttachmentsModalView.linkedDocuments);
      return;
    }
    this.modalState()?.open.set(false);
  }

  private _selectableList() {
    return this.sectionsHelper().filterHelper.filter === UPLOADED_DOCUMENTS_KEY ? null : this.documentsList();
  }
}
